import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/Button";
import { apiService } from "../../services/api";
import { TeamBuilderConfig } from "./TeamBuilderConfig";
import { TeamColumn } from "./TeamColumn";
import toast from "react-hot-toast";
import {
  XMarkIcon,
  ArrowPathIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  sortPlayersByScore,
  distributeRanked,
  distributeBalanced,
  distributeHybrid,
  calculateTeamStats,
  filterPlayers,
  movePlayer,
} from "../../utils/teamDistribution";

const TeamBuilderPanel = ({
  isOpen,
  onClose,
  evalId,
  playerResults,
  onSaveComplete,
}) => {
  // Configuration state
  const [numTeams, setNumTeams] = useState(4);
  const [teamGroupings, setTeamGroupings] = useState([
    { startTeam: 1, endTeam: 4, mode: "balanced" },
  ]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [includeUnscored, setIncludeUnscored] = useState(false);

  // Teams state
  const [teams, setTeams] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Get unique groups from players
  const availableGroups = [
    ...new Set(playerResults.map((p) => p.group).filter(Boolean)),
  ].sort();

  // Reset when panel closes
  useEffect(() => {
    if (!isOpen) {
      setTeams([]);
      setHasGenerated(false);
      setHasChanges(false);
      setSelectedPlayer(null);
    }
  }, [isOpen]);

  // Update groupings when numTeams changes
  useEffect(() => {
    setTeamGroupings([{ startTeam: 1, endTeam: numTeams, mode: "balanced" }]);
  }, [numTeams]);

  const handleGenerate = useCallback(() => {
    // Filter players
    const filteredPlayers = filterPlayers(playerResults, {
      groups: selectedGroups.length > 0 ? selectedGroups : null,
      includeUnscored,
    });

    // Sort by score
    const sortedPlayers = sortPlayersByScore(filteredPlayers);

    // Distribute based on groupings
    let distributedTeams;

    // Check if we have a simple single grouping
    if (
      teamGroupings.length === 1 &&
      teamGroupings[0].startTeam === 1 &&
      teamGroupings[0].endTeam === numTeams
    ) {
      distributedTeams =
        teamGroupings[0].mode === "ranked"
          ? distributeRanked(sortedPlayers, numTeams)
          : distributeBalanced(sortedPlayers, numTeams);
    } else {
      distributedTeams = distributeHybrid(
        sortedPlayers,
        teamGroupings,
        numTeams,
      );
    }

    setTeams(distributedTeams);
    setHasGenerated(true);
    setHasChanges(true);
    setSelectedPlayer(null);
  }, [playerResults, numTeams, teamGroupings, selectedGroups, includeUnscored]);

  const handleMovePlayer = useCallback(
    (playerId, fromTeamIndex, toTeamIndex) => {
      if (fromTeamIndex === toTeamIndex) return;

      const newTeams = movePlayer(teams, playerId, fromTeamIndex, toTeamIndex);
      setTeams(newTeams);
      setHasChanges(true);
      setSelectedPlayer(null);
    },
    [teams],
  );

  const handleRenameTeam = useCallback(
    (teamIndex, newName) => {
      const newTeams = teams.map((team, i) =>
        i === teamIndex ? { ...team, name: newName } : team,
      );
      setTeams(newTeams);
      setHasChanges(true);
    },
    [teams],
  );

  const handlePlayerClick = useCallback(
    (player, teamIndex) => {
      if (selectedPlayer && selectedPlayer.id === player.id) {
        // Deselect if clicking the same player
        setSelectedPlayer(null);
      } else if (selectedPlayer) {
        // Move selected player to this team
        const fromTeamIndex = teams.findIndex((t) =>
          t.players.some((p) => p.id === selectedPlayer.id),
        );
        if (fromTeamIndex !== -1 && fromTeamIndex !== teamIndex) {
          handleMovePlayer(selectedPlayer.id, fromTeamIndex, teamIndex);
        }
        setSelectedPlayer(null);
      } else {
        // Select this player
        setSelectedPlayer(player);
      }
    },
    [selectedPlayer, teams, handleMovePlayer],
  );

  const handleSave = async () => {
    if (!hasChanges || teams.length === 0) return;

    setSaving(true);
    try {
      // Build assignments array
      const assignments = [];
      teams.forEach((team) => {
        team.players.forEach((player) => {
          assignments.push({
            playerId: player.id,
            team: team.name,
          });
        });
      });

      await apiService.updateTeamAssignments(evalId, assignments);
      toast.success(`Saved team assignments for ${assignments.length} players`);
      setHasChanges(false);
      if (onSaveComplete) onSaveComplete();
    } catch (error) {
      console.error("Save team assignments error:", error);
      toast.error("Failed to save team assignments");
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  // Calculate summary stats
  const teamStats = teams.map((team) => calculateTeamStats(team));
  const totalAssigned = teams.reduce((sum, t) => sum + t.players.length, 0);
  const filteredCount = filterPlayers(playerResults, {
    groups: selectedGroups.length > 0 ? selectedGroups : null,
    includeUnscored,
  }).length;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute inset-y-0 right-0 flex max-w-full">
        <div className="relative w-screen max-w-5xl">
          <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
            {/* Header */}
            <div className="bg-slate-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    Team Builder
                  </h2>
                  <p className="text-sm text-slate-100">
                    Assign players to teams based on evaluation scores
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md text-slate-200 hover:text-white focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Configuration */}
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <TeamBuilderConfig
                numTeams={numTeams}
                setNumTeams={setNumTeams}
                teamGroupings={teamGroupings}
                setTeamGroupings={setTeamGroupings}
                selectedGroups={selectedGroups}
                setSelectedGroups={setSelectedGroups}
                includeUnscored={includeUnscored}
                setIncludeUnscored={setIncludeUnscored}
                availableGroups={availableGroups}
              />
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {filteredCount} players available
                  {hasGenerated && ` | ${totalAssigned} assigned`}
                </div>
                <Button onClick={handleGenerate}>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  {hasGenerated ? "Regenerate Teams" : "Generate Teams"}
                </Button>
              </div>
            </div>

            {/* Teams Preview */}
            <div className="flex-1 overflow-auto p-6">
              {!hasGenerated ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <p className="text-lg">Configure your settings above</p>
                    <p className="text-sm">
                      Then click "Generate Teams" to preview the distribution
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {selectedPlayer && (
                    <div className="mb-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                      <span className="font-medium">{selectedPlayer.name}</span>{" "}
                      selected. Click on a team column to move them, or click
                      the player again to deselect.
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {teams.map((team, index) => (
                      <TeamColumn
                        key={index}
                        team={team}
                        teamIndex={index}
                        stats={teamStats[index]}
                        selectedPlayerId={selectedPlayer?.id}
                        onPlayerClick={handlePlayerClick}
                        onDropPlayer={(playerId, fromIndex) =>
                          handleMovePlayer(playerId, fromIndex, index)
                        }
                        onRenameTeam={(newName) =>
                          handleRenameTeam(index, newName)
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {hasChanges ? (
                    <span className="text-amber-600">Unsaved changes</span>
                  ) : hasGenerated ? (
                    <span className="text-green-600">All changes saved</span>
                  ) : null}
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || saving || teams.length === 0}
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Team Assignments"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TeamBuilderPanel };
